import React from 'react';
import PropTypes from 'prop-types';

import { OrderDetailsSummaryTemplate } from '@components/order-summary/order-details-summary-template';
import { ORDER_TYPES } from '@nandosaus/constants';
import { Divider } from '@components/divider';
import { Modal } from '../modal';
import { Button } from '../button';
import { Flex, Box } from '../grid';
import { P, Subheading } from '../typography';
import { Alert } from '../alert';

const CancelCateringOrderModal = ({
  open,
  action,
  onClose,
  secondaryButtonText,
  primaryButtonText,
  isLoading,
  order,
  success,
  error,
}) => {
  const {
    formattedCateringDate,
    formattedPickupTime,
    formattedRestaurantAddress,
    id,
    formattedTotal,
    restaurantDetails,
    loyaltyAmountCents,
    isCateringDeliveryOrder,
  } = order;
  // loyaltyAmountCents is flag-gated (0 when partial redemption is off), so a positive value means
  // points were redeemed on this order and have been restored as part of the cancellation refund.
  const hadPointsApplied = loyaltyAmountCents > 0;
  return (
    <Modal
      open={open}
      handleClose={onClose}
      modalTitle=""
      hasFooterDivider={false}
      webHeight="auto"
      width={{ _: '90%', md: '576px' }}
      height="auto"
      borderRadius="24px"
      isDividerHidden
    >
      <Flex
        textAlign="center"
        flexDirection="column"
        gap={{ xs: '24px', md: '32px' }}
        px={{ xs: '12px', lg: '82px' }}
        py="16px"
        pt="0"
      >
        <Flex flexDirection="column" alignItems="center" gap="8px" px={{ xs: '28px', lg: '12px' }}>
          <Subheading mb={0} fontWeight="600" color="black" variant={2}>
            {success ? `Order cancelled` : `Are you sure you want to cancel?`}
          </Subheading>

          <P variant={2}>
            {success ? (
              <>
                {hadPointsApplied
                  ? 'Your catering order has been cancelled. Your points have been restored and your card refund is on its way.'
                  : 'If you have any questions or need further assistance, don’t hesitate to contact our customer care team.'}
                {hadPointsApplied && (
                  <>
                    <br />
                    <br />
                    If you have any questions, our customer care team is here to help.
                  </>
                )}
              </>
            ) : (
              'Dont’ worry, we won’t hold it against you. But if you have changed your mind, just hit the ‘Go Back’ button below.'
            )}
          </P>
        </Flex>

        {!success && (
          <Flex
            flexDirection="column"
            borderRadius="20px"
            py="20px"
            px={{ xs: '18px', md: '24px' }}
            background="white"
            width="100%"
          >
            <Flex flexDirection="row" width="100%" justifyContent="space-between" flexGrow="1">
              <Flex flexDirection="column" gap="2px">
                <P textAlign="start" variant={5} fontWeight="bold">
                  ORDER NUMBER
                </P>
                <P variant={3}>{id?.length > 10 ? 'coming soon' : id}</P>
              </Flex>

              <Flex flexDirection="column" gap="2px">
                <P textAlign="start" variant={5} fontWeight="bold">
                  ORDERED
                </P>
                <P variant={3}>{formattedCateringDate}</P>
              </Flex>

              <Flex flexDirection="column" gap="2px">
                <P textAlign="start" variant={5} fontWeight="bold">
                  TOTAL
                </P>
                <P variant={3}>{formattedTotal}</P>
              </Flex>
            </Flex>
            <Divider variant="lightGrey" my={{ xs: '20px', md: '24px' }} />

            <Flex mb="12px" flexDirection="column">
              <OrderDetailsSummaryTemplate
                orderType={isCateringDeliveryOrder ? ORDER_TYPES.CATERING_DELIVERY : ORDER_TYPES.CATERING}
                location={{
                  streetDetails: restaurantDetails.name,
                  suburbDetails: formattedRestaurantAddress,
                }}
                orderDetail={formattedPickupTime}
                error={null}
                isCheckout
              />
            </Flex>
          </Flex>
        )}

        {error && !success && (
          <Box mb="16px">
            <Alert type="error">{error}</Alert>
          </Box>
        )}

        <Flex width="100%" gap={{ xs: '8px', md: '16px' }} justifyContent="center" alignItems="center">
          {!success ? (
            <>
              <Button
                isLoading={isLoading}
                variant="primaryOutlined"
                size="small"
                onClick={onClose}
                width={{ xs: '100%', md: 'fit-content' }}
              >
                {secondaryButtonText}
              </Button>
              <Button
                isLoading={isLoading}
                variant="primary"
                onClick={action}
                width={{ xs: '100%', md: 'fit-content' }}
              >
                {primaryButtonText}
              </Button>
            </>
          ) : (
            <Button isLoading={isLoading} variant="primary" onClick={onClose} width="130px">
              OK
            </Button>
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};

CancelCateringOrderModal.propTypes = {
  open: PropTypes.bool,
  action: PropTypes.func,
  onClose: PropTypes.func,
  primaryButtonText: PropTypes.string,
  secondaryButtonText: PropTypes.string,
  isLoading: PropTypes.bool,
  order: PropTypes.object.isRequired,
  success: PropTypes.bool,
  error: PropTypes.string,
};

CancelCateringOrderModal.defaultProps = {
  open: false,
  action: () => {},
  onClose: () => {},
  primaryButtonText: 'YES, CANCEL ORDER',
  secondaryButtonText: 'NO, GO BACK',
  isLoading: false,
  success: false,
  error: null,
};

export { CancelCateringOrderModal };
